import React, { useMemo, useState } from 'react';
import { ProductTypeEnum } from '@koob/enums';
import { getDayItemSearchLocation } from '../logic/getDayItemSearchLocation';
import useTripStore from '../store/useTripStore';
import {
  useDay,
  useExperience,
  useSelectedDay,
  useSelectedType,
  useSelectDay,
  useResetDaySelection,
  useSetTripFilters,
} from '../hooks/useTripStoreSelectors';

interface TripDayItemSelectorProps {
  dayIndex: number;
  type: string;
  isIncluded?: boolean;
  onSecondaryClick?: () => void;
  addProgramLabel: string;
  addHotelLabel: string;
  exploreIncludedHotelsLabel: string;
  exploreSuggestedHotelsLabel: string;
  noProgramLabel: string;
  orLabel: string;
  bookOnMyOwnLabel: string;
}

export default function TripDayItemSelector({
  dayIndex,
  type,
  isIncluded,
  onSecondaryClick,
  addProgramLabel,
  addHotelLabel,
  exploreIncludedHotelsLabel,
  exploreSuggestedHotelsLabel,
  noProgramLabel,
  orLabel,
  bookOnMyOwnLabel,
}: TripDayItemSelectorProps) {
  const selectedDay = useSelectedDay();
  const selectedType = useSelectedType();
  const selectDay = useSelectDay();
  const resetDaySelection = useResetDaySelection();
  const setTripFilters = useSetTripFilters();

  const day = useDay(dayIndex);
  const updateDay = useTripStore((state) => state.updateDay);
  const experiencesMap = useTripStore((state) => state.experiences);
  const days = useTripStore((state) => state.days);
  const setBookOnMyOwn = (value: boolean) => {
    updateDay(dayIndex, { hotelBookedOnMyOwn: value });
  };

  const experience = useExperience(dayIndex);

  const [exploreHotels, setExploreHotels] = useState(true);

  const program = experience?.programs?.[(day?.experienceDayIndex ?? 1) - 1];

  const isSelected = selectedDay === dayIndex && selectedType === type;

  const hasSuggestedHotels =
    program?.accomodationHotel || program?.alternativeHotels?.length > 0;

  const onSelect = () => {
    if (!isSelected) {
      setTripFilters((current) => ({
        ...current,
        location: getDayItemSearchLocation({
          type,
          days,
          dayIndex,
          experiencesMap,
          experience,
          program,
        }),
      }));
      selectDay(dayIndex, type);
    } else if (onSecondaryClick && exploreHotels && hasSuggestedHotels) {
      onSecondaryClick();
      setExploreHotels(false);
    } else {
      resetDaySelection();
      setExploreHotels(true);
    }
  };

  const isProgramSlot = type === ProductTypeEnum.EXPERIENCE;

  const selectorLabel = useMemo(() => {
    if (type === ProductTypeEnum.EXPERIENCE) {
      return addProgramLabel;
    }

    if (isSelected && exploreHotels && hasSuggestedHotels) {
      return isIncluded
        ? exploreIncludedHotelsLabel
        : exploreSuggestedHotelsLabel;
    }

    return addHotelLabel;
  }, [
    type,
    isSelected,
    isIncluded,
    exploreHotels,
    hasSuggestedHotels,
    addProgramLabel,
    addHotelLabel,
    exploreIncludedHotelsLabel,
    exploreSuggestedHotelsLabel,
  ]);

  return (
    <div
      id={`day-${dayIndex}-${type}-selector`}
      onClick={() => onSelect()}
      className={[
        'h-full p-5 rounded-md border-2 transition',
        // KD-3581 AC09 — a day with no program is a dashed skeleton, not a
        // filled block: after an experience is removed the timeline keeps its
        // length and each freed day says so. It stays clickable, because this
        // selector is also how a program is put back.
        isProgramSlot
          ? 'border-dashed bg-white/40'
          : 'bg-gray-100 border-solid',
        isSelected
          ? [
              'animate-pulse',
              isIncluded ? 'border-green-500' : 'border-orange-500',
            ].join(' ')
          : [
              'cursor-pointer hover:border-gray-300',
              isProgramSlot ? 'border-gray-300' : 'border-transparent',
            ].join(' '),
      ].join(' ')}
    >
      <div className="h-full flex justify-center items-center">
        <div className="text-gray-400 text-sm text-center">
          {isProgramSlot && (
            <div className="mb-1 text-gray-500">{noProgramLabel}</div>
          )}

          {selectorLabel}

          {type === ProductTypeEnum.HOTEL && (
            <>
              <div className="my-1 text-gray-400 text-xs">— {orLabel} —</div>

              <button
                className="border-none text-gray-400 italic hover:text-orange-500 transition"
                onClick={(event) => {
                  event.stopPropagation();
                  setBookOnMyOwn(true);
                }}
              >
                {bookOnMyOwnLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
